import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import { getCampStatus } from '../../utils/campLogic';

export default function Assembly() {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  const { isAAP, currentTrailDay, windowOpen } = getCampStatus(user, settings);
  const isLockedOut = !windowOpen;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resContent, resSettings] = await Promise.all([
          api.get('camp_content_fbwm'),
          api.get('settings_fbwm')
        ]);
        
        const settingsData = Array.isArray(resSettings) ? resSettings : resSettings.data || [];
        const sMap = {};
        settingsData.forEach(s => {
          if (s.setting_key) sMap[s.setting_key] = s.value;
        });
        setSettings(sMap);

        const data = Array.isArray(resContent) ? resContent : resContent.data || [];
        const filtered = data
          .filter(item => item.module_type === 'assembly')
          .filter(item => item.is_active === 1 || item.is_active === true || item.is_active === '1')
          .sort((a, b) => a.day_number - b.day_number);

        setContent(filtered);
      } catch (err) {
        console.error("Failed to load assembly content", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-center text-muted flex flex-col items-center justify-center min-h-[400px]">
        <SafeIcon name="Loader" className="animate-spin text-4xl text-amber mb-4" />
        <p>Loading Morning Assembly...</p>
      </div>
    );
  }

  if (isLockedOut) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-forest-green shadow-xl bg-[#EAF1E6]">
          <div className="w-24 h-24 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-6 shadow-md text-white">
            <SafeIcon name="Video" className="text-5xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-4 font-poppins">Assembly Access Expired</h1>
          <p className="text-lg text-forest-green font-medium mb-6 max-w-2xl mx-auto">
            {settings.replay_expiry_msg || "Free pass replay access has expired. Upgrade to All-Access to continue watching all camp sessions."}
          </p>
          <a href="/#/upgrade">
            <button className="bg-amber text-white font-bold px-8 py-3 rounded hover:bg-orange-600 transition-colors">
              Upgrade to All-Access
            </button>
          </a>
        </Card>
      </div>
    );
  }

  // Pre-camp view
  if (currentTrailDay === 0 && user?.early_access != 1 && user?.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-tan shadow-sm">
          <div className="w-20 h-20 bg-amber/20 rounded-full flex items-center justify-center mx-auto mb-6 text-amber">
            <SafeIcon name="Clock" className="text-4xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-4 font-poppins">Camp Hasn't Started Yet</h1>
          <p className="text-lg text-muted mb-6 max-w-2xl mx-auto">
            Morning Assemblies unlock at 6:00 AM EST each day of camp. Check back on Day 1 to watch the first session!
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-fadeIn">
      <header className="border-b-2 border-tan pb-6 mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-amber/20 rounded-full flex items-center justify-center text-amber">
            <SafeIcon name="Video" className="text-2xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green font-poppins">Morning Assembly</h1>
        </div>
        <p className="text-muted text-lg max-w-3xl">
          Start your camp day here. These short, focused 15-minute video sessions set the mindset and calling foundation for the day's work.
        </p>
      </header>

      {!isAAP && (
        <div className="bg-[#fdfaf6] border border-tan rounded-lg p-5 flex items-start gap-4 mb-8">
          <SafeIcon name="Info" className="text-amber mt-1 shrink-0" />
          <div className="text-sm text-dark-green leading-relaxed">
            <span className="font-bold">Replay Access:</span> Free pass holders have access to all session replays until{' '}
            <strong>{settings.replay_expiry
              ? new Date(settings.replay_expiry).toLocaleString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric',
                  hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
                })
              : 'the end of the replay window'}</strong>. Upgrade to All-Access for lifetime replays.
          </div>
        </div>
      )}

      {content.length === 0 ? (
        <Card className="text-center py-12 text-muted">
          No Assembly sessions have been published yet.
        </Card>
      ) : (
        <div className="space-y-12">
          {content.map(session => {
            const isUnlocked = user?.role === 'admin' || user?.early_access == 1 || currentTrailDay >= session.day_number;

            return (
              <Card key={session.id} className={`overflow-hidden border-2 transition-all ${isUnlocked ? 'border-forest-green shadow-md' : 'border-tan opacity-75'}`}>
                <div className="flex flex-col md:flex-row gap-6 p-6">
                  {/* Video Thumbnail / Player Area */}
                  <div className="w-full md:w-5/12 shrink-0">
                    <div className="aspect-video bg-black rounded-md overflow-hidden relative shadow-inner group">
                      {isUnlocked ? (
                        session.video_url ? (
                          <iframe 
                            src={session.video_url} 
                            className="w-full h-full border-0 absolute inset-0"
                            allow="autoplay; fullscreen; picture-in-picture" 
                            allowFullScreen
                          ></iframe>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-500 flex-col gap-2">
                            <SafeIcon name="VideoOff" className="text-3xl" />
                            <span className="text-sm font-medium">Video currently unavailable</span>
                          </div>
                        )
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-dark-green/90 p-4 text-center">
                          <SafeIcon name="Lock" className="text-4xl mb-3 text-amber" />
                          <span className="font-bold text-lg">Unlocks Day {session.day_number}</span>
                          <span className="text-sm opacity-80 mt-1">6:00 AM local time</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className="flex-1 flex flex-col">
                    <div className="mb-2 inline-flex items-center gap-2 text-xs font-bold text-forest-green uppercase tracking-widest bg-forest-green/10 px-3 py-1 rounded-full w-fit">
                      Day {session.day_number}
                    </div>
                    <h2 className="text-2xl font-bold text-dark-green mb-3 font-poppins leading-tight">
                      {session.title || `Assembly Day ${session.day_number}`}
                    </h2>
                    
                    {session.description && (
                      <div className="text-gray-700 leading-relaxed mb-6 whitespace-pre-wrap flex-1">
                        {session.description}
                      </div>
                    )}

                    {!isUnlocked && (
                      <div className="mt-auto pt-4 border-t border-tan text-sm text-muted font-medium flex items-center gap-2">
                        <SafeIcon name="Clock" className="text-amber" />
                        This session will be available when Day {session.day_number} begins.
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}